import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Building2, Briefcase, ChevronDown, X, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_CONFIG = {
  projects: {
    placeholder: 'Project',
    placeholderAr: 'المشروع',
    apiPath: '/api/intelligence/projects',
    icon: Briefcase,
    getName: (item) => item.canonical_name || item.name || '',
  },
  companies: {
    placeholder: 'Company',
    placeholderAr: 'الشركة',
    apiPath: '/api/intelligence/companies',
    icon: Building2,
    getName: (item) => item.canonical_name || item.name || '',
  },
};

/**
 * LinkedEntityFilter — multi-select toggle popover for filtering by linked projects or companies.
 * Mirrors the Popover + Checkbox interaction pattern of IndustryMultiSelectFilter.
 *
 * @param {string} entityType — 'projects' | 'companies'
 * @param {Array} selected — array of { _id, name }
 * @param {function} onChange — called with new array of { _id, name }
 * @param {string} language
 * @param {boolean} isRTL
 * @param {string} token
 * @param {string} testId
 */
const LinkedEntityFilter = ({
  entityType,
  selected = [],
  onChange,
  language = 'en',
  isRTL = false,
  token,
  testId,
}) => {
  const cfg = ENTITY_CONFIG[entityType];
  const Icon = cfg.icon;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchItems = useCallback(async (q) => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}${cfg.apiPath}?page_size=40${q ? `&search=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [cfg.apiPath, token]);

  // Initial fetch when popover opens
  useEffect(() => {
    if (open && items.length === 0) fetchItems('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchItems(val), 300);
  };

  const selectedSet = useMemo(() => new Set(selected.map((s) => s._id)), [selected]);
  const selectedMap = useMemo(() => {
    const m = {};
    selected.forEach((s) => { m[s._id] = s.name; });
    return m;
  }, [selected]);

  const toggleItem = (item) => {
    const name = cfg.getName(item);
    if (selectedSet.has(item._id)) {
      onChange(selected.filter((s) => s._id !== item._id));
    } else {
      onChange([...selected, { _id: item._id, name }]);
    }
  };

  const removeOne = (id, e) => {
    e?.stopPropagation();
    onChange(selected.filter((s) => s._id !== id));
  };

  const clearAll = () => onChange([]);

  const placeholder = language === 'ar' ? cfg.placeholderAr : cfg.placeholder;
  const count = selected.length;

  const triggerText = () => {
    if (count === 0) return placeholder;
    if (count === 1) return selected[0].name;
    return `${count} ${language === 'ar' ? 'محدد' : 'selected'}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId || `${entityType}-link-filter`}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[120px]">{triggerText()}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[360px] max-w-[calc(100vw-16px)] p-0" align="end" avoidCollisions={true}>
        {/* Header: search + clear */}
        <div className="p-2 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
              className="h-8 pl-7 text-sm"
              data-testid={testId ? `${testId}-search` : `${entityType}-link-filter-search`}
            />
          </div>
          {count > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                {count} {language === 'ar' ? 'محدد' : 'selected'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-6 text-xs px-2"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                {language === 'ar' ? 'مسح' : 'Clear'}
              </Button>
            </div>
          )}
        </div>

        <ScrollArea className="h-[320px]">
          <div className="p-2 space-y-0.5">
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {language === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}
              </div>
            ) : (
              <>
                {/* Currently-selected items not in current fetched list still need to be listed/toggleable */}
                {selected
                  .filter((s) => !items.some((i) => i._id === s._id))
                  .map((s) => (
                    <div
                      key={`sel-${s._id}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors bg-primary/10"
                      onClick={() => removeOne(s._id)}
                      data-testid={testId ? `${testId}-selected-${s._id}` : undefined}
                    >
                      <Checkbox checked className="pointer-events-none" />
                      <span className="text-sm flex-1 leading-snug">{s.name}</span>
                    </div>
                  ))}

                {items.map((item) => {
                  const isSel = selectedSet.has(item._id);
                  return (
                    <div
                      key={item._id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                        isSel ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => toggleItem(item)}
                      data-testid={testId ? `${testId}-option-${item._id}` : `${entityType}-link-filter-option-${item._id}`}
                    >
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggleItem(item)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm flex-1 leading-snug">{cfg.getName(item)}</span>
                    </div>
                  );
                })}

                {!loading && items.length === 0 && selected.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {language === 'ar' ? 'لا توجد نتائج' : 'No results found.'}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Selected chips footer */}
        {count > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((s) => (
                <Badge key={s._id} variant="secondary" className="text-xs">
                  {(selectedMap[s._id] || s.name).slice(0, 14)}
                  <X
                    className="w-3 h-3 ml-1 cursor-pointer"
                    onClick={(e) => removeOne(s._id, e)}
                  />
                </Badge>
              ))}
              {count > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{count - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default LinkedEntityFilter;
